import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  IBloquePlantilla,
  IPantallaPlantilla,
  IPlantillaBotonera,
  TipoBloquePlantilla,
} from 'modelos/src';
import {
  bloqueNuevo,
  botonesDePantalla,
  esItemEspecial,
  FONDO_TEMA,
  IMAGENES_BLOQUE,
  ITEMS_ESPECIALES,
  PANTALLAS,
  pantallasDe,
  TIPOS_BLOQUE,
  VERSION_PLANTILLA,
} from '../../../../auxiliares/plantillas/plantilla-manual';

/** Un botón de la categoría, tal como lo necesita el editor para elegirlo. */
export interface BotonElegible {
  /** `_tmpId` del form: `_id` real o `tmp-btn-N` (se resuelve al guardar). */
  id: string;
  nombre: string;
  funcion?: string;
  color?: string;
}

/**
 * Editor de la plantilla de botonera ARMADA A MANO. Una solapa por cada una de las 3 pantallas y,
 * en cada una, la lista de piezas (bloques) que se agregan, reordenan y configuran. En las piezas
 * Botones y Fila se eligen los botones de la categoría uno por uno.
 *
 * No muta lo que recibe: cada cambio emite una plantilla nueva por `plantillaChange` (el padre la
 * guarda en el control `plantillaBotonera` y re-sincroniza el reparto de botones).
 */
@Component({
  selector: 'app-plantilla-manual-editor',
  templateUrl: './plantilla-manual-editor.component.html',
  styleUrls: ['./plantilla-manual-editor.component.scss'],
  standalone: false,
})
export class PlantillaManualEditorComponent {
  @Input() plantilla?: IPlantillaBotonera | null;
  /** Botones de la categoría (los de la sección "Botones de la categoría"). */
  @Input() botones: BotonElegible[] = [];
  /** El cliente tiene sirenas: si no, reflector y sirena de las filas no se dibujan. */
  @Input() sirenas = false;
  /** Color del tema del cliente: el inicial al elegir "Otro color" de fondo. */
  @Input() colorTema = '#FB972E';
  /** Pantalla que se está editando (0..2). La comparte con el preview. */
  @Input() pantalla = 0;
  @Output() pantallaChange = new EventEmitter<number>();
  @Output() plantillaChange = new EventEmitter<IPlantillaBotonera>();

  public infoPantallas = PANTALLAS;
  public tipos = TIPOS_BLOQUE;
  public imagenes = IMAGENES_BLOQUE;
  public especiales = ITEMS_ESPECIALES;
  public columnas = [1, 2, 3];

  get pantallas(): IPantallaPlantilla[] {
    return pantallasDe(this.plantilla);
  }

  get bloques(): IBloquePlantilla[] {
    return this.pantallas[this.pantalla]?.bloques ?? [];
  }

  public cantidadPiezas(i: number): number {
    return this.pantallas[i]?.bloques?.length ?? 0;
  }

  public setPantalla(i: number): void {
    this.pantalla = i;
    this.pantallaChange.emit(i);
  }

  public tipo(t: TipoBloquePlantilla) {
    return this.tipos.find((x) => x.tipo === t);
  }

  // ===================== Piezas =====================

  private emitir(bloques: IBloquePlantilla[]): void {
    const pantallas = this.pantallas.map((p, i) =>
      i === this.pantalla ? { ...p, bloques } : p,
    );
    this.plantillaChange.emit({ version: VERSION_PLANTILLA, pantallas });
  }

  public agregar(tipo: TipoBloquePlantilla): void {
    this.emitir([...this.bloques, bloqueNuevo(tipo)]);
  }

  public quitar(i: number): void {
    this.emitir(this.bloques.filter((_, j) => j !== i));
  }

  public mover(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.bloques.length) return;
    const out = [...this.bloques];
    [out[i], out[j]] = [out[j], out[i]];
    this.emitir(out);
  }

  public cambiar(i: number, cambios: Partial<IBloquePlantilla>): void {
    this.emitir(
      this.bloques.map((b, j) => (j === i ? { ...b, ...cambios } : b)),
    );
  }

  /** Fondo detrás de una imagen: ninguno, el color del tema o uno elegido. */
  public tipoFondo(b: IBloquePlantilla): 'nada' | 'tema' | 'otro' {
    if (!b.colorFondo) return 'nada';
    return b.colorFondo === FONDO_TEMA ? 'tema' : 'otro';
  }

  public setTipoFondo(i: number, valor: 'nada' | 'tema' | 'otro'): void {
    const actual = this.bloques[i].colorFondo;
    const colorFondo =
      valor === 'tema'
        ? FONDO_TEMA
        : valor === 'otro'
          ? actual && actual !== FONDO_TEMA
            ? actual
            : this.colorTema
          : undefined;
    this.cambiar(i, { colorFondo });
  }

  /**
   * Color elegido en un ngx-colors (el mismo selector que el resto del admin). Solo guarda hex
   * `#rrggbb`: es lo que el nativo sabe leer y lo que deja pasar `limpiarPlantilla`.
   */
  public setColor(i: number, campo: 'color' | 'colorFondo', valor: string): void {
    const hex = /^#[0-9a-f]{6}/i.exec((valor ?? '').trim())?.[0]?.toLowerCase();
    if (!hex || this.bloques[i]?.[campo] === hex) return;
    this.cambiar(i, { [campo]: hex });
  }

  /** Inputs numéricos: vacío = sin valor (p.ej. alto automático de los botones). */
  public numero(valor: string): number | undefined {
    if (valor === '' || valor === null || valor === undefined) return undefined;
    const n = Number(valor);
    return Number.isFinite(n) ? n : undefined;
  }

  // ===================== Listas de botones / ítems =====================

  /** Campo con la lista ordenada de la pieza: `idsBotones` (Botones) o `items` (Fila). */
  private campoLista(b: IBloquePlantilla): 'idsBotones' | 'items' {
    return b.tipo === 'fila' ? 'items' : 'idsBotones';
  }

  public lista(b: IBloquePlantilla): string[] {
    return b[this.campoLista(b)] ?? [];
  }

  public agregarALista(i: number, id: string): void {
    const b = this.bloques[i];
    this.cambiar(i, { [this.campoLista(b)]: [...this.lista(b), id] });
  }

  public quitarDeLista(i: number, k: number): void {
    const b = this.bloques[i];
    this.cambiar(i, {
      [this.campoLista(b)]: this.lista(b).filter((_, j) => j !== k),
    });
  }

  public moverEnLista(i: number, k: number, delta: number): void {
    const b = this.bloques[i];
    const out = [...this.lista(b)];
    const j = k + delta;
    if (j < 0 || j >= out.length) return;
    [out[k], out[j]] = [out[j], out[k]];
    this.cambiar(i, { [this.campoLista(b)]: out });
  }

  /** Botones de la categoría que todavía no están en esta pieza. */
  public disponibles(b: IBloquePlantilla): BotonElegible[] {
    const ya = new Set(this.lista(b));
    return this.botones.filter((x) => !ya.has(x.id));
  }

  /**
   * Piezas fijas que todavía no están en esta fila y que sirven en esta pantalla: "Atrás" no en la
   * 1 (no hay anterior) y "Más" no en la 3 (no hay siguiente).
   */
  public especialesDisponibles(b: IBloquePlantilla) {
    const ya = new Set(this.lista(b));
    const ultima = this.infoPantallas.length - 1;
    return this.especiales.filter(
      (x) =>
        !ya.has(x.id) &&
        !(x.id === 'atras' && this.pantalla === 0) &&
        !(x.id === 'mas' && this.pantalla === ultima),
    );
  }

  public etiqueta(id: string): string {
    if (esItemEspecial(id)) {
      return this.especiales.find((x) => x.id === id)?.label ?? id;
    }
    const b = this.botones.find((x) => x.id === id);
    return b ? b.nombre : 'Botón quitado';
  }

  public esEspecial(id: string): boolean {
    return esItemEspecial(id);
  }

  public existe(id: string): boolean {
    return esItemEspecial(id) || this.botones.some((x) => x.id === id);
  }

  /** Otra pantalla donde ya está este botón (se muestra en la primera donde aparece). */
  public usadoEnOtra(id: string): number | null {
    for (let i = 0; i < this.pantalla; i++) {
      if (botonesDePantalla(this.pantallas[i]).includes(id)) return i + 1;
    }
    return null;
  }

  // ===================== Avisos =====================

  /** "Más" sin pantalla siguiente con piezas: el vecino no lo ve. */
  get masSinDestino(): boolean {
    const siguiente = this.pantallas[this.pantalla + 1];
    const usaMas = this.bloques.some(
      (b) => b.tipo === 'fila' && b.items?.includes('mas'),
    );
    return usaMas && !siguiente?.bloques?.length;
  }

  /** Pantalla 2/3 con piezas pero sin "Atrás": se vuelve solo con el back del teléfono. */
  get sinAtras(): boolean {
    return (
      this.pantalla > 0 &&
      this.bloques.length > 0 &&
      !this.bloques.some((b) => b.tipo === 'fila' && b.items?.includes('atras'))
    );
  }

  /** Pantalla 2/3 con piezas a la que no se llega: ninguna fila de la anterior tiene "Más". */
  get inalcanzable(): boolean {
    if (this.pantalla === 0 || !this.bloques.length) return false;
    const anterior = this.pantallas[this.pantalla - 1];
    return !(anterior?.bloques ?? []).some(
      (b) => b.tipo === 'fila' && b.items?.includes('mas'),
    );
  }

  public trackIndex = (i: number): number => i;
}
